import { motion } from "framer-motion";
import { Seo } from "@/seo/Seo";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { Counter } from "@/components/common/Counter";
import { DetailHero } from "@/components/detail/DetailHero";
import { PillList } from "@/components/detail/PillList";
import { FeatureGrid, BenefitGrid } from "@/components/detail/FeatureGrid";
import { WorkflowTimeline } from "@/components/detail/WorkflowTimeline";
import { TechStackChips } from "@/components/detail/TechStackChips";
import { IndustryDashboard } from "@/components/detail/IndustryDashboard";
import { FaqAccordion } from "@/components/detail/FaqAccordion";
import { CaseStudyPreview } from "@/components/detail/CaseStudyPreview";
import { RelatedLinks, type RelatedLink } from "@/components/detail/RelatedLinks";
import { DetailCta } from "@/components/detail/DetailCta";
import { serviceDetails } from "@/data/serviceDetails";
import { solutionDetails } from "@/data/solutionDetails";
import { DEV_WORKFLOW } from "@/data/detail.shared";
import { staggerContainer, fadeUp, viewportOnce } from "@/animations/motion";
import type { IndustryDetail } from "@/types/industryDetail";

function serviceLinks(slugs: string[]): RelatedLink[] {
  return slugs
    .map((s) => serviceDetails.find((x) => x.slug === s))
    .filter(Boolean)
    .map((x) => ({ label: x!.title, description: x!.category, to: `/services/${x!.slug}`, icon: x!.icon }));
}
function solutionLinks(slugs: string[]): RelatedLink[] {
  return slugs
    .map((s) => solutionDetails.find((x) => x.slug === s))
    .filter(Boolean)
    .map((x) => ({ label: x!.title, description: x!.domain, to: `/solutions/${x!.slug}`, icon: x!.icon }));
}

export function IndustryDetailTemplate({ data }: { data: IndustryDetail }) {
  const words = data.name.split(" ");
  const titleLines = words.length > 1 ? [words.slice(0, -1).join(" "), words.slice(-1).join(" ")] : [data.name];

  return (
    <>
      <Seo title={`${data.name} Software Solutions`} description={data.tagline} path={`/industries/${data.slug}`} />
      <DetailHero
        category={`Industries · ${data.sector}`}
        titleLines={titleLines}
        tagline={data.tagline}
        icon={data.icon}
        accent={data.accent}
        image={data.image}
      />

      {/* Overview + dashboard mockup */}
      <Section spacing="compact">
        <Container>
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <SectionHeading eyebrow="Overview" title={`Understanding ${data.name}`} />
              <Reveal>
                <p className="mt-6 text-lg leading-relaxed text-muted-foreground">{data.overview}</p>
              </Reveal>
              <div className="mt-8">
                <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Typical users</h3>
                <div className="mt-4"><PillList items={data.typicalUsers} variant="chip" /></div>
              </div>
            </div>
            <Reveal delay={0.1}><IndustryDashboard name={data.name} accent={data.accent} kpis={data.outcomes} /></Reveal>
          </div>
        </Container>
      </Section>

      {/* Pain points + manual processes */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <SectionHeading eyebrow="Current challenges" title="Business pain points" />
              <div className="mt-8"><PillList items={data.painPoints} columns={1} /></div>
            </div>
            <div>
              <SectionHeading eyebrow="Today" title="Common manual processes" />
              <div className="mt-8"><PillList items={data.manualProcesses} columns={1} /></div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Opportunities */}
      <Section spacing="compact">
        <Container>
          <SectionHeading eyebrow="Digital transformation" title="Where technology changes the game" description="High-impact opportunities to modernise how this sector operates." />
          <div className="mt-12"><FeatureGrid features={data.opportunities} /></div>
        </Container>
      </Section>

      {/* Sample workflow */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading eyebrow="How it works" title="A sample business workflow" description="Click any step to see what happens at each stage." />
          <div className="mt-10"><WorkflowTimeline nodes={data.workflow} accent={data.accent} /></div>
        </Container>
      </Section>

      {/* Benefits + outcomes */}
      <Section spacing="compact">
        <Container>
          <SectionHeading eyebrow="Key benefits" title="What you can expect" />
          <div className="mt-10"><BenefitGrid benefits={data.benefits} /></div>
          <div className="mt-8 grid grid-cols-3 gap-px overflow-hidden rounded-[1.5rem] border border-border bg-border">
            {data.outcomes.map((o) => (
              <div key={o.label} className="bg-card p-6 text-center sm:p-8">
                <Counter value={o.value} className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl" />
                <p className="mt-2 text-xs text-muted-foreground sm:text-sm">{o.label}</p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Tech stack */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading eyebrow="Technology" title="Suggested technology stack" />
          <div className="mt-10"><TechStackChips items={data.techStack} /></div>
        </Container>
      </Section>

      {/* Implementation approach */}
      <Section spacing="compact">
        <Container>
          <SectionHeading eyebrow="Getting there" title="Our implementation approach" description="A proven, low-risk path from discovery to launch and beyond." />
          <div className="mt-10"><WorkflowTimeline nodes={DEV_WORKFLOW} accent={data.accent} /></div>
        </Container>
      </Section>

      {/* Recommended services + solutions */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading eyebrow="Recommended" title="Services that fit this industry" />
          <div className="mt-8"><RelatedLinks items={serviceLinks(data.recommendedServices)} /></div>
          <h3 className="mt-14 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Recommended solutions</h3>
          <div className="mt-6"><RelatedLinks items={solutionLinks(data.recommendedSolutions)} /></div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section spacing="compact">
        <Container>
          <SectionHeading eyebrow="FAQ" title="Frequently asked questions" align="center" />
          <div className="mt-10"><FaqAccordion faqs={data.faqs} /></div>
        </Container>
      </Section>

      {/* Case study */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading eyebrow="Concept work" title="How this comes to life" description="A concept project illustrating the kind of outcome we deliver in this sector." />
          <div className="mt-10"><CaseStudyPreview data={data.caseConcept} /></div>
        </Container>
      </Section>

      <DetailCta origin={{ type: "industry", slug: data.slug, label: data.name }} />
    </>
  );
}
